import { amber, cream, green, ink, navy, red, sand } from './reference'

/**
 * Semantic tokens: meaning, not color. Both modes answer to the same names, so
 * a component styled once is legible in either — dark mode is a re-pointing,
 * not a redesign. Values and the L* targets per role are the lab's measured
 * mapping (lab-missing-pieces.html §1: text/primary ≈ L*13, tertiary ≈ L*45,
 * canvas ≈ L*97).
 */
export type SemanticTokens = {
  /** canvas behind everything */
  bg: string
  /** raised card / paper */
  surface: string
  /** sunken panel, input wash, skeleton base */
  surface2: string
  /** text/primary */
  ink: string
  /** text/secondary */
  ink2: string
  /** text/tertiary — the darkest step that still reads as "muted" and passes 4.5:1 */
  ink3: string
  /** hairline borders and dividers */
  line: string
  /** selected row / item tint. 1.05:1 by design: a checked control is the redundant cue */
  selection: string
  good: string
  goodBg: string
  danger: string
  dangerBg: string
  /** star fill — always paired with sigStroke, never meaning-bearing alone */
  star: string
  /** the darkened-amber outline that lets the star mark pass 3:1 where the fill cannot */
  sigStroke: string
  /** focus ring */
  focus: string
}

export const semantic: { light: SemanticTokens; dark: SemanticTokens } = {
  light: {
    bg: sand[50],
    surface: sand[0],
    surface2: sand[100],
    ink: sand[900],
    ink2: sand[700],
    ink3: sand[500],
    line: 'rgba(36, 34, 28, 0.10)',
    selection: cream[100],
    good: green[600],
    goodBg: green[100],
    danger: red[600],
    dangerBg: red[100],
    star: amber[500],
    sigStroke: amber[600],
    focus: navy[600],
  },
  dark: {
    bg: sand[950],
    surface: sand[925],
    surface2: sand[850],
    ink: sand[25],
    ink2: sand[300],
    ink3: sand[400],
    line: 'rgba(242, 239, 229, 0.10)',
    selection: 'rgba(255, 201, 5, 0.10)',
    good: green[300],
    goodBg: green[950],
    danger: red[300],
    dangerBg: 'rgba(241, 152, 142, 0.12)',
    star: amber[400],
    sigStroke: amber[600],
    focus: '#8FAEE0',
  },
}

// ink[900] and amber[500] are referenced by the action tokens in palette.ts;
// re-export so palette has one import site for the whole token source.
export { amber, cream, green, ink, navy, red, sand }
